import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { featureCodesApi } from '../services/api'

interface FeatureCode {
  id: string
  name: string
  code: string
  description: string
  category: string
  is_enabled: boolean
  is_system: boolean
}

const categories = [
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'call_forward', label: 'Rufumleitung' },
  { value: 'dnd', label: 'Nicht stören' },
  { value: 'recording', label: 'Aufnahme' },
  { value: 'parking', label: 'Parken' },
  { value: 'transfer', label: 'Weiterleitung' },
  { value: 'conference', label: 'Konferenz' },
  { value: 'info', label: 'Info' },
  { value: 'other', label: 'Sonstiges' }
]

export default function FeatureCodes() {
  const [features, setFeatures] = useState<FeatureCode[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingFeature, setEditingFeature] = useState<FeatureCode | null>(null)
  const [formData, setFormData] = useState({
    code: '',
    is_enabled: true
  })

  useEffect(() => {
    loadFeatures()
  }, [])

  const loadFeatures = async () => {
    try {
      const response = await featureCodesApi.list()
      setFeatures(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openEditModal = (feature: FeatureCode) => {
    setEditingFeature(feature)
    setFormData({
      code: feature.code,
      is_enabled: feature.is_enabled
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingFeature) return
    try {
      await featureCodesApi.update(editingFeature.id, formData)
      toast.success('Feature-Code aktualisiert')
      setShowModal(false)
      loadFeatures()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const toggleFeature = async (feature: FeatureCode) => {
    try {
      await featureCodesApi.update(feature.id, { is_enabled: !feature.is_enabled })
      toast.success(`${feature.name} ${feature.is_enabled ? 'deaktiviert' : 'aktiviert'}`)
      loadFeatures()
    } catch {
      toast.error('Fehler beim Aktualisieren')
    }
  }

  const groupedFeatures = categories.map(cat => ({
    ...cat,
    features: features.filter(f => f.category === cat.value)
  })).filter(cat => cat.features.length > 0)

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Feature-Codes</h4>
      </div>

      {loading ? (
        <div className="text-center py-4">Laden...</div>
      ) : features.length === 0 ? (
        <div className="text-center py-4 text-muted">Keine Feature-Codes konfiguriert</div>
      ) : (
        groupedFeatures.map(category => (
          <Card key={category.value} className="mb-3">
            <Card.Header>
              <strong>{category.label}</strong>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th style={{width: '120px'}}>Code</th>
                    <th>Funktion</th>
                    <th>Beschreibung</th>
                    <th style={{width: '100px'}}>Status</th>
                    <th style={{width: '80px'}} className="text-end">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {category.features.map(f => (
                    <tr key={f.id}>
                      <td>
                        <code className="fs-6">{f.code}</code>
                      </td>
                      <td>
                        <strong>{f.name}</strong>
                        {f.is_system && <Badge bg="secondary" className="ms-2">System</Badge>}
                      </td>
                      <td className="text-muted">{f.description}</td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={f.is_enabled}
                          onChange={() => toggleFeature(f)}
                          label=""
                        />
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEditModal(f)}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ))
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Feature-Code bearbeiten</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Funktion</Form.Label>
              <Form.Control
                value={editingFeature?.name || ''}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Code *</Form.Label>
              <Form.Control
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                required
                placeholder="z.B. *72"
              />
              <Form.Text className="text-muted">
                Verwenden Sie * oder # gefolgt von Ziffern
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_enabled"
                label="Aktiviert"
                checked={formData.is_enabled}
                onChange={(e) => setFormData({...formData, is_enabled: e.target.checked})}
              />
            </Form.Group>

            {editingFeature?.is_system && (
              <div className="alert alert-info mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Dies ist ein System-Feature. Änderungen können die Grundfunktionalität beeinträchtigen.
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">Speichern</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
